import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { roleApi } from '@/services/permissionsRolesApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Eye, Edit, Trash2, UserCog, Search, BarChart3, Database, CheckCircle2, XCircle } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { Role, ListRolesParams } from '@/types/permissionsRoles';

export const RoleListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [minPermissionCount, setMinPermissionCount] = useState<string>('');
  const [maxPermissionCount, setMaxPermissionCount] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [initializeModalOpen, setInitializeModalOpen] = useState(false);
  const { hasRole } = useAdminAuth();

  const canAccess = hasRole('ADMIN') || hasRole('SYSTEM');
  const canInitialize = hasRole('SYSTEM_ADMIN');

  // Build fetch function with filters
  const fetchRolesFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      const listParams: ListRolesParams = {
        ...params,
        sort: `${sortBy},${sortDirection}`,
        active: filterStatus !== 'all' ? filterStatus === 'active' : undefined,
        systemRole: filterType !== 'all' ? filterType === 'system' : undefined,
        minPermissionCount: minPermissionCount ? parseInt(minPermissionCount, 10) : undefined,
        maxPermissionCount: maxPermissionCount ? parseInt(maxPermissionCount, 10) : undefined,
        search: searchQuery.trim() || undefined,
      };
      return roleApi.list(listParams);
    };
  }, [sortBy, sortDirection, filterStatus, filterType, minPermissionCount, maxPermissionCount, searchQuery]);

  // Use paginated list hook
  const {
    data: roles,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Role>({
    fetchFn: fetchRolesFn,
    entityName: 'roles',
    enabled: canAccess,
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (roleId: string) => {
      await roleApi.delete(roleId);
    },
    entityName: 'role',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ roleId, active }: { roleId: string; active: boolean }) => {
      await roleApi.toggleStatus(roleId, active);
    },
    entityName: 'role',
    operation: 'update',
    successMessage: (_data, variables) => `Role ${variables.active ? 'activated' : 'deactivated'} successfully`,
    onSuccess: () => {
      refetch();
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: async () => roleApi.initialize(),
    entityName: 'roles',
    operation: 'custom',
    successMessage: (result) => `Initialized ${result.created} role(s), ${result.skipped} already existed`,
    onSuccess: () => {
      setInitializeModalOpen(false);
      refetch();
    },
  });

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleDelete = async (roleId: string, roleName: string) => {
    if (!window.confirm(`Are you sure you want to delete role "${roleName}"?`)) return;
    await deleteMutation.mutate(roleId);
  };

  const handleToggleStatus = async (roleId: string, currentActive: boolean) => {
    await toggleStatusMutation.mutate({ roleId, active: !currentActive });
  };

  const handleInitialize = async () => {
    await initializeMutation.mutateAsync();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (active: boolean) => {
    return active
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  if (!canAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to view roles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Roles</h1>
          <p className="text-muted-foreground mt-2">Manage system and custom roles</p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/admin/roles/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Role
            </Link>
          </Button>
          {canInitialize && (
            <Button variant="outline" onClick={() => setInitializeModalOpen(true)}>
              <Database className="mr-2 h-4 w-4" />
              Initialize
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/roles/statistics">
              <BarChart3 className="mr-2 h-4 w-4" />
              Statistics
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Input
              type="number"
              placeholder="Min Permission Count"
              value={minPermissionCount}
              onChange={(e) => setMinPermissionCount(e.target.value)}
              min="0"
            />
            <Input
              type="number"
              placeholder="Max Permission Count"
              value={maxPermissionCount}
              onChange={(e) => setMaxPermissionCount(e.target.value)}
              min="0"
            />
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Roles ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all roles</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading roles..." />
          ) : (error || roles.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load roles. Please try again or create a new one." : "Get started by creating your first role."}
              icon={UserCog}
              actions={[
                {
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/roles/new'),
                  variant: 'default' as const,
                },
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Name
                      {sortBy === 'name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('permissionIds')}
                    >
                      Permission Count
                      {sortBy === 'permissionIds' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('active')}
                    >
                      Status
                      {sortBy === 'active' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell className="max-w-md truncate">
                        {role.description || '—'}
                      </TableCell>
                      <TableCell>{role.permissions?.length || 0}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(role.active)}`}>
                          {role.active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {role.systemRole ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            System
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                            Custom
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/roles/${role.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/roles/${role.id}/edit`}>
                              <Edit className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(role.id, role.active)}
                            title={role.active ? 'Deactivate' : 'Activate'}
                          >
                            {role.active ? (
                              <XCircle className="h-4 w-4 text-yellow-600" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                          {!role.systemRole && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(role.id, role.name)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                    </div>
                    <Select value={pagination.size.toString()} onValueChange={(v) => setSize(parseInt(v))}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Initialize Modal */}
      <Dialog open={initializeModalOpen} onOpenChange={setInitializeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Initialize System Roles</DialogTitle>
            <DialogDescription>
              This will create 3 system roles if they don't already exist. This operation is idempotent and safe to run multiple times.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {initializeMutation.isLoading ? (
              <LoadingState message="Initializing roles..." />
            ) : (
              <p className="text-sm text-muted-foreground">
                Click Confirm to proceed with initialization.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitializeModalOpen(false)} disabled={initializeMutation.isLoading}>
              Cancel
            </Button>
            <Button onClick={handleInitialize} disabled={initializeMutation.isLoading}>
              {initializeMutation.isLoading ? 'Initializing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleListPage;
